import { produce } from 'immer';
import { delay, put, takeLatest } from 'redux-saga/effects';
import type { ActionType } from 'typesafe-actions';
import { createReducer, createStandardAction } from 'typesafe-actions';
import { v4 as uuidv4 } from 'uuid';

import type { ToastData } from '@/components/ui/Toast';

type UIReducerState = {
  alarm: string | null;
  toasts: ToastData[];
  pendingToasts: ToastData[];
  spinnerRequestCount: number;
};

const MAX_TOASTS = 3;

const SHOW_SPINNER = 'ui/SHOW_SPINNER';
const HIDE_SPINNER = 'ui/HIDE_SPINNER';

export const showSpinner = createStandardAction(SHOW_SPINNER)();
export const hideSpinner = createStandardAction(HIDE_SPINNER)();

const SHOW_ALARM = 'ui/SHOW_ALARM';
const HIDE_ALARM = 'ui/HIDE_ALARM';

export const showAlarm = createStandardAction(SHOW_ALARM)<string>();
const hideAlarm = createStandardAction(HIDE_ALARM)();

const SHOW_TOAST = 'ui/SHOW_TOAST';
const HIDE_TOAST = 'ui/HIDE_TOAST';

export type ShowToastPayload = Pick<ToastData, 'toastType' | 'msg' | 'showClose'> & { id?: string };

export const uiActions = {
  showSpinner,
  hideSpinner,
  showAlarm,
  hideAlarm,
  showToast: createStandardAction(SHOW_TOAST)<ShowToastPayload>(),
  hideToast: createStandardAction(HIDE_TOAST)<{ id: string }>(),
};

function* hideAlarmAsync() {
  yield delay(2000);
  yield put(hideAlarm());
}

export function* uiSaga() {
  yield takeLatest(SHOW_ALARM, hideAlarmAsync);
}

export const uiReducer = createReducer<UIReducerState, ActionType<typeof uiActions>>(
  {
    alarm: null,
    toasts: [],
    pendingToasts: [],
    spinnerRequestCount: 0,
  },
  {
    [SHOW_SPINNER]: state => {
      return { ...state, spinnerRequestCount: state.spinnerRequestCount + 1 };
    },
    [HIDE_SPINNER]: state => {
      return { ...state, spinnerRequestCount: Math.max(0, state.spinnerRequestCount - 1) };
    },
    [SHOW_ALARM]: (state, { payload }) => ({ ...state, alarm: payload }),
    [HIDE_ALARM]: state => ({ ...state, alarm: null }),
    [SHOW_TOAST]: (state, { payload }) => {
      const newToast = {
        id: payload.id ?? uuidv4(),
        toastType: payload.toastType,
        msg: payload.msg,
        showClose: payload.showClose,
      };
      const nonLeaving = state.toasts.filter(t => !t.leaving);

      // 이미 큐가 있거나 보여줄 자리가 없으면 대기열에 추가
      if (nonLeaving.length >= MAX_TOASTS || state.pendingToasts.length > 0) {
        const updatedToasts =
          nonLeaving.length >= MAX_TOASTS
            ? state.toasts.map(t => (t.id === nonLeaving[nonLeaving.length - 1].id ? { ...t, leaving: true } : t))
            : state.toasts;
        return {
          ...state,
          toasts: updatedToasts,
          pendingToasts: [...state.pendingToasts, newToast],
        };
      }

      return { ...state, toasts: [newToast, ...state.toasts] };
    },
    [HIDE_TOAST]: (state, { payload }) =>
      produce(state, draft => {
        const index = draft.toasts.findIndex(toast => toast.id === payload.id);

        if (index > -1) {
          draft.toasts.splice(index, 1);
        }

        // 대기 중인 토스트가 있고 자리가 생기면 추가
        if (draft.pendingToasts.length > 0) {
          const nonLeaving = draft.toasts.filter(t => !t.leaving);
          if (nonLeaving.length < MAX_TOASTS) {
            const [next, ...rest] = draft.pendingToasts;
            draft.toasts.unshift(next);
            draft.pendingToasts = rest;
          }
        }
      }),
  }
);

export * from './selectors';
